import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import clsx from 'clsx'
import { apiUrl } from '../config/api'
import { isValidTituloEleitor } from '../utils/titulo'

interface FormConfig {
  name: string
  description: string | null
  require_name: boolean
  require_phone: boolean
  require_email: boolean
  require_cpf: boolean
  require_city: boolean
  require_full_address: boolean
  require_birth_date: boolean
  require_voter_reg: boolean
  submissions_count: number
}
interface OrgInfo { name: string; photo_url: string | null; political_name: string | null }
interface LeaderInfo { name: string; nickname: string | null; photo_url: string | null }
interface GeoItem { id: number; name: string; code?: string }

const maskPhone = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 11)
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').replace(/[-\s()]*$/, '')
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3')
}
const maskCPF = (v: string) => v.replace(/\D/g, '').slice(0, 11).replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4').replace(/[.-]*$/, '')
const maskCEP = (v: string) => v.replace(/\D/g, '').slice(0, 8).replace(/(\d{5})(\d{0,3})/, '$1-$2').replace(/-$/, '')
const onlyDigits = (v: string) => v.replace(/\D/g, '')

const inputCls = 'w-full h-12 px-4 rounded-xl border border-gray-300 bg-white text-gray-900 text-[15px] placeholder:text-gray-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 outline-none transition'

export default function RedeCapturePage() {
  const { hash } = useParams()
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [config, setConfig] = useState<FormConfig | null>(null)
  const [org, setOrg] = useState<OrgInfo | null>(null)
  const [leader, setLeader] = useState<LeaderInfo | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [justAdded, setJustAdded] = useState(false)
  const [error, setError] = useState('')
  const [count, setCount] = useState(0)
  // Config de campos (Settings → Formulário de Eleitor, contexto público)
  const [fieldCfg, setFieldCfg] = useState<Record<string, { visible: boolean; required: boolean }>>({})
  const hasCfg = Object.keys(fieldCfg).length > 0
  const showF = (key: string) => hasCfg ? (fieldCfg[key]?.visible ?? false) : true
  const reqF = (key: string) => hasCfg ? (fieldCfg[key]?.required ?? false) : false

  const [states, setStates] = useState<GeoItem[]>([])
  const [cities, setCities] = useState<GeoItem[]>([])
  const [stateId, setStateId] = useState('')
  const [zones, setZones] = useState<{ id: number; nr_zona: number }[]>([])
  const [sections, setSections] = useState<{ id: number; nr_secao: number; nm_local_votacao: string | null }[]>([])
  const [addressMode, setAddressMode] = useState<'cep' | 'manual'>('cep')
  const [cepCity, setCepCity] = useState('') // cidade/UF resolvida pelo CEP (exibição)
  const [cepLoading, setCepLoading] = useState(false)

  const [form, setForm] = useState({
    name: '', nickname: '', phone: '', email: '', cpf: '', sus_number: '', birth_date: '', gender: '',
    voter_registration: '', tse_zone_id: '', tse_section_id: '',
    zip_code: '', street: '', number: '', neighborhood: '', complement: '', geo_city_id: '', notes: '',
  })
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    setLoading(true)
    fetch(apiUrl(`/v1/public/forms/${hash}`), { headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(res => {
        const f = res.data.form || {}
        // require_* vêm como 0/1 (tinyint) — normalizar para boolean (senão o 0 é renderizado no label)
        setConfig({
          ...f,
          require_name: !!f.require_name,
          require_phone: !!f.require_phone,
          require_email: !!f.require_email,
          require_cpf: !!f.require_cpf,
          require_city: !!f.require_city,
          require_full_address: !!f.require_full_address,
          require_birth_date: !!f.require_birth_date,
          require_voter_reg: !!f.require_voter_reg,
        })
        setFieldCfg(res.data.field_config || {})
        setOrg(res.data.organization)
        setLeader(res.data.leader || null)
        setCount(f.submissions_count || 0)
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [hash])

  useEffect(() => {
    if (!config) return
    fetch(apiUrl(`/v1/public/forms/${hash}/geo/states`), { headers: { Accept: 'application/json' } })
      .then(r => r.json()).then(res => setStates(res.data || [])).catch(() => {})
  }, [config, hash])

  useEffect(() => {
    if (!stateId) { setCities([]); return }
    fetch(apiUrl(`/v1/public/forms/${hash}/geo/states/${stateId}/cities`), { headers: { Accept: 'application/json' } })
      .then(r => r.json()).then(res => setCities(res.data || [])).catch(() => setCities([]))
  }, [stateId, hash])

  // Zonas eleitorais quando muda a cidade
  useEffect(() => {
    setZones([]); setSections([])
    setForm(f => ({ ...f, tse_zone_id: '', tse_section_id: '' }))
    if (!form.geo_city_id) return
    fetch(apiUrl(`/v1/public/forms/${hash}/elections/zones?city_id=${form.geo_city_id}`), { headers: { Accept: 'application/json' } })
      .then(r => r.json()).then(res => setZones(res.data || [])).catch(() => setZones([]))
  }, [form.geo_city_id, hash])

  function onZoneChange(zoneId: string) {
    setForm(f => ({ ...f, tse_zone_id: zoneId, tse_section_id: '' }))
    setSections([])
    if (!zoneId) return
    fetch(apiUrl(`/v1/public/forms/${hash}/elections/zones/${zoneId}/sections`), { headers: { Accept: 'application/json' } })
      .then(r => r.json()).then(res => setSections(res.data || [])).catch(() => setSections([]))
  }

  // Consulta o CEP automaticamente assim que completa 8 dígitos
  useEffect(() => {
    if (onlyDigits(form.zip_code).length === 8) handleCep()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.zip_code])

  async function handleCep() {
    const cep = onlyDigits(form.zip_code)
    if (cep.length < 8) return
    setCepLoading(true)
    try {
      const res = await fetch(apiUrl(`/v1/public/forms/${hash}/geo/cep/${cep}`), { headers: { Accept: 'application/json' } }).then(r => r.json())
      const d = res.data
      if (d) {
        setForm(f => ({ ...f, street: d.street || f.street, neighborhood: d.neighborhood || f.neighborhood, geo_city_id: d.geo_city_id ? String(d.geo_city_id) : f.geo_city_id }))
        setCepCity(d.city_name && d.state_code ? `${d.city_name}/${d.state_code}` : '')
      }
    } catch { /* */ } finally { setCepLoading(false) }
  }

  async function submit() {
    if (submitting) return
    setError('')
    if (!form.name.trim()) return setError('Informe o nome.')
    if (onlyDigits(form.phone).length < 10) return setError('Informe um telefone válido.')
    if (form.voter_registration && !isValidTituloEleitor(form.voter_registration)) return setError('Título de eleitor inválido — confira os números.')
    // Obrigatórios conforme a config pública
    const checks: Record<string, { ok: () => boolean; label: string }> = {
      nickname:           { ok: () => !!form.nickname.trim(), label: 'Apelido' },
      cpf:                { ok: () => onlyDigits(form.cpf).length >= 11, label: 'CPF' },
      birth_date:         { ok: () => !!form.birth_date, label: 'Data de nascimento' },
      gender:             { ok: () => !!form.gender, label: 'Gênero' },
      email:              { ok: () => !!form.email.trim(), label: 'E-mail' },
      voter_registration: { ok: () => !!form.voter_registration, label: 'Título de eleitor' },
      address:            { ok: () => !!form.geo_city_id, label: 'Cidade' },
      electoral_section:  { ok: () => !!form.tse_zone_id && !!form.tse_section_id, label: 'Zona e seção' },
    }
    const miss = Object.entries(checks).filter(([k, c]) => reqF(k) && showF(k) && !c.ok()).map(([, c]) => c.label)
    if (miss.length) return setError(`Preencha: ${miss.join(', ')}`)
    setSubmitting(true)
    try {
      const payload: any = {
        name: form.name || undefined,
        nickname: form.nickname || undefined,
        phone: onlyDigits(form.phone) || undefined,
        email: form.email || undefined,
        cpf: onlyDigits(form.cpf) || undefined,
        sus_number: onlyDigits(form.sus_number) || undefined,
        birth_date: form.birth_date || undefined,
        gender: form.gender || undefined,
        zip_code: onlyDigits(form.zip_code) || undefined,
        street: form.street || undefined,
        number: form.number || undefined,
        neighborhood: form.neighborhood || undefined,
        complement: form.complement || undefined,
        geo_city_id: form.geo_city_id ? Number(form.geo_city_id) : undefined,
        voter_registration: form.voter_registration || undefined,
        tse_zone_id: form.tse_zone_id ? Number(form.tse_zone_id) : undefined,
        tse_section_id: form.tse_section_id ? Number(form.tse_section_id) : undefined,
        notes: form.notes || undefined,
      }
      const res = await fetch(apiUrl(`/v1/public/forms/${hash}/submit`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const e = await res.json().catch(() => null)
        throw new Error(e?.error || e?.message || 'Erro ao enviar')
      }
      setCount(c => c + 1)
      setModalOpen(false)
      setJustAdded(true)
      setForm(f => ({ ...f, name: '', nickname: '', phone: '', email: '', cpf: '', sus_number: '', birth_date: '', gender: '', voter_registration: '', tse_zone_id: '', tse_section_id: '', notes: '' }))
    } catch (e: any) {
      setError(e.message || 'Erro ao enviar. Tente novamente.')
    } finally { setSubmitting(false) }
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-8 h-8 border-4 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
    </div>
  }

  if (notFound || !config) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6 text-center">
      <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4 text-2xl">🔗</div>
      <h1 className="text-lg font-semibold text-gray-800">Link indisponível</h1>
      <p className="text-gray-500 text-sm mt-1">Este link de cadastro não existe ou foi desativado.</p>
    </div>
  }

  const headerName = leader ? (leader.nickname || leader.name) : (org?.political_name || org?.name)
  const headerPhoto = leader?.photo_url || org?.photo_url

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-6">
          {headerPhoto
            ? <img src={headerPhoto} alt="" className="w-20 h-20 rounded-full object-cover mb-3 ring-4 ring-white shadow" />
            : <div className="w-20 h-20 rounded-full bg-indigo-500 text-white flex items-center justify-center text-2xl font-bold mb-3 shadow">
                {(headerName || '?').charAt(0).toUpperCase()}
              </div>}
          <h1 className="text-xl font-bold text-gray-900">{headerName}</h1>
        </div>

        {/* Resumo da rede */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-sm text-gray-500">Sua rede possui</p>
          <p className="text-5xl font-bold text-indigo-600 my-1 tabular-nums">{count.toLocaleString('pt-BR')}</p>
          <p className="text-sm text-gray-500">{count === 1 ? 'pessoa' : 'pessoas'}</p>

          {justAdded && (
            <p className="mt-4 text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">✓ Pessoa adicionada à sua rede!</p>
          )}

          <button onClick={() => { setError(''); setJustAdded(false); setModalOpen(true) }}
            className="mt-6 w-full h-12 rounded-xl bg-indigo-500 text-white font-semibold hover:bg-indigo-600 transition flex items-center justify-center gap-2">
            <span className="text-xl leading-none">+</span> Adicionar pessoa
          </button>
        </div>
      </div>

      {/* Modal do formulário */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4" onClick={() => setModalOpen(false)}>
          <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-base font-semibold text-gray-900">Adicionar pessoa</h2>
              <button onClick={() => setModalOpen(false)} className="w-8 h-8 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex items-center justify-center text-2xl leading-none">×</button>
            </div>
            <div className="p-6">
              <div className="space-y-3.5">
            {/* Dados pessoais */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome completo {reqF('name') ? <span className="text-red-500">*</span> : null}</label>
                <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Nome completo" />
              </div>
              {showF('nickname') && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Apelido {reqF('nickname') ? <span className="text-red-500">*</span> : null}</label>
                <input className={inputCls} value={form.nickname} onChange={e => set('nickname', e.target.value)} placeholder="Como é conhecido(a)" />
              </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefone / WhatsApp {reqF('phone') ? <span className="text-red-500">*</span> : null}</label>
                <input className={inputCls} value={form.phone} onChange={e => set('phone', maskPhone(e.target.value))} placeholder="(00) 00000-0000" inputMode="tel" />
              </div>
              {showF('email') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">E-mail {reqF('email') ? <span className="text-red-500">*</span> : null}</label>
                <input className={inputCls} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@exemplo.com" />
              </div>
              )}
              {showF('cpf') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">CPF {reqF('cpf') ? <span className="text-red-500">*</span> : null}</label>
                <input className={inputCls} value={form.cpf} onChange={e => set('cpf', maskCPF(e.target.value))} placeholder="000.000.000-00" inputMode="numeric" />
              </div>
              )}
              {showF('birth_date') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Data de nascimento {reqF('birth_date') ? <span className="text-red-500">*</span> : null}</label>
                <input className={inputCls} type="date" value={form.birth_date} onChange={e => set('birth_date', e.target.value)} />
              </div>
              )}
              {showF('gender') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Gênero {reqF('gender') ? <span className="text-red-500">*</span> : null}</label>
                <select className={inputCls} value={form.gender} onChange={e => set('gender', e.target.value)}>
                  <option value="">--</option>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                  <option value="O">Outro</option>
                </select>
              </div>
              )}
              {showF('sus_number') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Cartão SUS {reqF('sus_number') ? <span className="text-red-500">*</span> : null}</label>
                <input className={inputCls} value={form.sus_number} onChange={e => set('sus_number', onlyDigits(e.target.value).slice(0, 15))} placeholder="000 0000 0000 0000" inputMode="numeric" />
              </div>
              )}
            </div>

            {/* Endereço */}
            {showF('address') && (
            <div className="border-t border-gray-100 pt-4 space-y-3.5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Endereço {reqF('address') ? <span className="text-red-500">*</span> : null}</p>
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                  <button type="button" onClick={() => setAddressMode('cep')} className={clsx('px-3 py-1 rounded-md text-xs font-medium transition-colors', addressMode === 'cep' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500')}>Por CEP</button>
                  <button type="button" onClick={() => setAddressMode('manual')} className={clsx('px-3 py-1 rounded-md text-xs font-medium transition-colors', addressMode === 'manual' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500')}>Cidade/estado</button>
                </div>
              </div>

              {addressMode === 'cep' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">CEP</label>
                    <div className="relative">
                      <input className={inputCls} value={form.zip_code} onChange={e => set('zip_code', maskCEP(e.target.value))} placeholder="00000-000" inputMode="numeric" />
                      {cepLoading && <span className="absolute right-3 top-3.5 w-5 h-5 border-2 border-indigo-200 border-t-indigo-500 rounded-full animate-spin" />}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Rua</label>
                      <input className={inputCls} value={form.street} onChange={e => set('street', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Número</label>
                      <input className={inputCls} value={form.number} onChange={e => set('number', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Complemento</label>
                    <input className={inputCls} value={form.complement} onChange={e => set('complement', e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Cidade {reqF('address') ? <span className="text-red-500">*</span> : null}</label>
                      <input readOnly value={cepCity} placeholder="Preencha o CEP" className={clsx(inputCls, 'bg-gray-50 text-gray-600 cursor-not-allowed')} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Bairro</label>
                      <input className={inputCls} value={form.neighborhood} onChange={e => set('neighborhood', e.target.value)} />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">UF {reqF('address') ? <span className="text-red-500">*</span> : null}</label>
                      <select className={inputCls} value={stateId} onChange={e => setStateId(e.target.value)}>
                        <option value="">--</option>
                        {states.map(s => <option key={s.id} value={s.id}>{s.code || s.name}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Cidade {reqF('address') ? <span className="text-red-500">*</span> : null}</label>
                      <select className={inputCls} value={form.geo_city_id} onChange={e => set('geo_city_id', e.target.value)} disabled={!stateId}>
                        <option value="">{stateId ? 'Selecione' : 'Escolha a UF'}</option>
                        {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Bairro</label>
                    <input className={inputCls} value={form.neighborhood} onChange={e => set('neighborhood', e.target.value)} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Rua</label>
                      <input className={inputCls} value={form.street} onChange={e => set('street', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Número</label>
                      <input className={inputCls} value={form.number} onChange={e => set('number', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Complemento</label>
                    <input className={inputCls} value={form.complement} onChange={e => set('complement', e.target.value)} />
                  </div>
                </>
              )}
            </div>
            )}

            {/* Dados eleitorais */}
            {(showF('voter_registration') || showF('electoral_section')) && (
            <div className="border-t border-gray-100 pt-4 space-y-3.5">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Dados eleitorais</p>
              {showF('voter_registration') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Título de eleitor {reqF('voter_registration') ? <span className="text-red-500">*</span> : null}</label>
                <input
                  className={clsx(inputCls, form.voter_registration.length === 12 && (isValidTituloEleitor(form.voter_registration) ? 'border-green-400 focus:border-green-400 focus:ring-green-500/10' : 'border-red-400 focus:border-red-400 focus:ring-red-500/10'))}
                  value={form.voter_registration}
                  onChange={e => set('voter_registration', onlyDigits(e.target.value).slice(0, 12))}
                  placeholder="000000000000"
                  inputMode="numeric"
                />
                {form.voter_registration.length === 12 && (
                  <p className={clsx('text-xs mt-1', isValidTituloEleitor(form.voter_registration) ? 'text-green-600' : 'text-red-600')}>
                    {isValidTituloEleitor(form.voter_registration) ? '✓ Título válido' : '✗ Título inválido — confira os números'}
                  </p>
                )}
              </div>
              )}
              {showF('electoral_section') && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Zona {reqF('electoral_section') ? <span className="text-red-500">*</span> : null}</label>
                  <select className={inputCls} value={form.tse_zone_id} onChange={e => onZoneChange(e.target.value)} disabled={!form.geo_city_id}>
                    <option value="">{form.geo_city_id ? (zones.length ? 'Selecione' : 'Sem zonas') : 'Escolha a cidade'}</option>
                    {zones.map(z => <option key={z.id} value={z.id}>Zona {z.nr_zona}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Seção {reqF('electoral_section') ? <span className="text-red-500">*</span> : null}</label>
                  <select className={inputCls} value={form.tse_section_id} onChange={e => set('tse_section_id', e.target.value)} disabled={!form.tse_zone_id}>
                    <option value="">{form.tse_zone_id ? 'Selecione' : 'Escolha a zona'}</option>
                    {sections.map(s => <option key={s.id} value={s.id}>Seção {s.nr_secao}{s.nm_local_votacao ? ` — ${s.nm_local_votacao}` : ''}</option>)}
                  </select>
                </div>
              </div>
              )}
            </div>
            )}

            {/* Observações */}
            {showF('notes') && (
            <div className="border-t border-gray-100 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Observações {reqF('notes') ? <span className="text-red-500">*</span> : null}</label>
              <textarea className={clsx(inputCls, 'h-auto py-2.5 resize-none')} rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Anotações (opcional)" />
            </div>
            )}

            {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

            <button onClick={submit} disabled={submitting}
              className="w-full h-12 mt-2 rounded-xl bg-indigo-500 text-white font-semibold hover:bg-indigo-600 disabled:opacity-60 transition flex items-center justify-center gap-2">
              {submitting ? <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : 'Adicionar'}
            </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
